import { useEffect, useState } from "react";
import { Mail, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const TEMPLATES = [
  { key: "confirm", label: "Conferma / emissione" },
  { key: "clarify", label: "Chiarimenti / modifiche" },
  { key: "free", label: "Testo libero" },
];

function summary(d) {
  const veh = [d.vehicle_brand, d.vehicle_model].filter(Boolean).join(" ");
  const n = Number(d.installments_count) || 1;
  const lines = [
    `Cliente: ${d.client_name || "—"}${d.client_fiscal_code ? ` (CF ${d.client_fiscal_code})` : ""}`,
    (veh || d.vehicle_plate) && `Veicolo: ${[veh, d.vehicle_plate].filter(Boolean).join(" · targa ")}`,
    `Compagnia: ${d.company || "—"}`,
    d.quote_number && `Preventivo: ${d.quote_number}${d.offer_number ? ` · Offerta ${d.offer_number}` : ""}`,
    `Premio: ${formatCurrency(d.per_rata)}${n > 1 ? ` × ${n} rate (${d.frazionamento}) = ${formatCurrency(d.amount)}` : ` (${d.frazionamento})`}`,
    d.effective_date && `Decorrenza: ${d.effective_date}`,
  ].filter(Boolean);
  const covs = (d.coverages || []).map((c) => `  - ${c.name}${c.premium != null ? `: ${formatCurrency(c.premium)}` : ""}`);
  return [...lines, covs.length ? `Garanzie:\n${covs.join("\n")}` : null].filter(Boolean).join("\n");
}

function buildTemplate(key, d) {
  const ref = [d.client_name, d.vehicle_plate].filter(Boolean).join(" - ");
  const hello = `Gentile ${d.partner_name || "partner"},`;
  const sign = `Cordiali saluti,\n${d.agent_name || ""}`.trim();
  if (key === "confirm") return {
    subject: `Conferma offerta${ref ? ` - ${ref}` : ""} - richiesta emissione`,
    body: `${hello}\n\nvi confermiamo che il cliente accetta l'offerta sotto riportata: vi chiediamo di procedere con l'emissione della polizza.\n\n${summary(d)}\n\nRestiamo in attesa della documentazione contrattuale.\n\n${sign}`,
  };
  if (key === "clarify") return {
    subject: `Richiesta chiarimenti offerta${ref ? ` - ${ref}` : ""}`,
    body: `${hello}\n\nin merito all'offerta sotto riportata vi chiediamo i seguenti chiarimenti / modifiche:\n\n- \n\n${summary(d)}\n\nGrazie per la collaborazione.\n\n${sign}`,
  };
  return { subject: "", body: "" };
}

const splitEmails = (s) => (s || "").split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

export function VariantPartnerEmailDialog({ offerId, variant, open, onOpenChange, onSent }) {
  const [ctx, setCtx] = useState(null);
  const [tpl, setTpl] = useState("confirm");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !variant) return;
    setCtx(null); setTpl("confirm"); setCc(""); setAttachPdf(true);
    api.get(`/quote-offers/${offerId}/variants/${variant.id}/partner-email-defaults`)
      .then(({ data }) => {
        setCtx(data); setTo(data.default_to || "");
        const t = buildTemplate("confirm", data); setSubject(t.subject); setBody(t.body);
      })
      .catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
  }, [open, variant, offerId, onOpenChange]);

  const applyTpl = (k) => { setTpl(k); const t = buildTemplate(k, ctx); setSubject(t.subject); setBody(t.body); };

  const send = async () => {
    const toL = splitEmails(to);
    if (!toL.length) { toast.error("Inserisci almeno un destinatario"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Oggetto e messaggio sono obbligatori"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/quote-offers/${offerId}/variants/${variant.id}/send-partner-email`, {
        to: toL, cc: splitEmails(cc), subject, body, attach_pdf: attachPdf, template: tpl,
      });
      toast.success(`Email inviata a ${toL.join(", ")}`);
      onSent?.(data.entry);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-sky-600" /> Invia email al partner</DialogTitle>
          <DialogDescription>{variant?.company}{ctx?.partner_name && ctx.partner_name !== variant?.company ? ` · ${ctx.partner_name}` : ""}</DialogDescription>
        </DialogHeader>
        {!ctx ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-1.5 flex-wrap" data-testid="partner-email-templates">
              {TEMPLATES.map((t) => (
                <button key={t.key} type="button" onClick={() => applyTpl(t.key)} data-testid={`partner-email-tpl-${t.key}`}
                  className={`h-7 px-2.5 rounded-full text-xs font-medium border transition-colors ${tpl === t.key ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div>
              <Label className="text-xs">Destinatario/i</Label>
              <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@partner.it" data-testid="partner-email-to" />
              {ctx.candidates?.length > 0 && (
                <div className="mt-1 flex gap-1 flex-wrap">
                  {ctx.candidates.map((c) => (
                    <button key={c.email} type="button" onClick={() => setTo(c.email)} title={c.label} data-testid={`partner-email-candidate-${c.email}`}
                      className="text-[10.5px] px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100">
                      {c.label}: {c.email}
                    </button>
                  ))}
                </div>
              )}
              {!ctx.candidates?.length && <p className="text-[11px] text-amber-700 mt-1">Nessuna email in anagrafica broker/compagnia: inseriscila a mano.</p>}
            </div>
            <div>
              <Label className="text-xs">CC (separati da virgola)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="opzionale" data-testid="partner-email-cc" />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="partner-email-subject" />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="font-mono text-xs" data-testid="partner-email-body" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} data-testid="partner-email-attach-pdf" />
              Allega il PDF della variante
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="partner-email-cancel">Annulla</Button>
          <Button onClick={send} disabled={!ctx || sending} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="partner-email-send">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default VariantPartnerEmailDialog;
